import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import axios from "axios";
import { REVIEWS_SEED } from "@/content";
import { useLang } from "@/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  es: {
    eyebrow: "Reseñas de Google",
    title: "Nuestros Huéspedes Nos Encantan",
    reviewsOn: "reseñas en Google",
    readAll: "Leer todas las reseñas en Google",
    google: "Google",
  },
  en: {
    eyebrow: "Google Reviews",
    title: "Loved by Our Guests",
    reviewsOn: "reviews on Google",
    readAll: "Read all reviews on Google",
    google: "Google",
  },
};

function Stars({ n = 5, className = "w-4 h-4" }) {
  return (
    <span className="flex items-center gap-0.5" aria-label={`${n} / 5`}>
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} className={`${className} text-goldbright`} fill="currentColor" strokeWidth={0} />
      ))}
    </span>
  );
}

export default function Reviews() {
  const { lang } = useLang();
  const t = COPY[lang];
  const [data, setData] = useState({
    reviews: REVIEWS_SEED,
    rating: 5.0,
    total: 42,
    link: "",
  });

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((r) => {
        const d = r.data;
        if (Array.isArray(d.reviews) && d.reviews.length) {
          setData({
            reviews: d.reviews,
            rating: d.rating,
            total: d.total,
            link: d.link || "",
          });
        }
      })
      .catch(() => {});
  }, []);

  if (!data.reviews?.length) return null;

  const fmt = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString(lang === "es" ? "es-ES" : "en-GB", {
      month: "short",
      year: "numeric",
    });
  };

  return (
    <section
      id="resenas"
      className="relative py-24 lg:py-28 bg-pine scroll-mt-16"
      data-testid="reviews-section"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <p className="font-mono text-[10px] sm:text-xs tracking-[0.3em] uppercase text-goldbright flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-9 bg-gold/70 inline-block" />
            {t.eyebrow}
            <span className="h-px w-9 bg-gold/70 inline-block" />
          </p>
          <h2 className="font-serif font-medium text-4xl sm:text-5xl text-cream leading-[1.08]">
            {t.title}
          </h2>
          <div className="mt-6 flex items-center justify-center gap-4">
            <span className="font-serif text-5xl text-cream" data-testid="reviews-rating">
              {data.rating.toFixed(1)}
            </span>
            <span className="flex flex-col items-start gap-1">
              <Stars />
              <span className="font-mono text-[11px] tracking-wider text-sage" data-testid="reviews-total">
                {data.total} {t.reviewsOn}
              </span>
            </span>
          </div>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.reviews.slice(0, 6).map((rv, i) => (
            <motion.blockquote
              key={i}
              data-testid={`review-card-${i}`}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="bg-moss rounded-2xl border border-gold/15 p-7 flex flex-col h-full hover:border-gold/45 transition-colors duration-500"
            >
              <Stars n={rv.rating || 5} className="w-3.5 h-3.5" />
              <p className="mt-4 text-sm text-cream/90 font-light leading-relaxed flex-1">
                “{rv.text}”
              </p>
              {rv.text_es && (
                <p
                  className="mt-3 text-[13px] italic text-sage font-light leading-relaxed"
                  data-testid={`review-translation-${i}`}
                >
                  {rv.text_es}
                </p>
              )}
              <footer className="mt-5 flex items-center justify-between gap-3 border-t border-gold/15 pt-4">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-sage">
                  {t.google} · {fmt(rv.date)}
                </span>
              </footer>
            </motion.blockquote>
          ))}
        </div>

        {data.link && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="mt-12 text-center"
          >
            <a
              href={data.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-google-link"
              className="group inline-flex items-center gap-2.5 rounded-full border border-gold/40 text-goldbright px-7 py-3.5 text-sm font-medium hover:bg-gold/10 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
            >
              {t.readAll}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>
        )}
      </div>
    </section>
  );
}
